import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { saveBookingReturnPath } from '../../../util/bookingIntent';
import { useFreeForListener } from '../../FirstSessionFree/FirstSessionFree';
import { rememberFreeOffer } from '../../../util/promoFirstFree';
import { skillOffersFromListing, offerMenuFromListing, isListeningOffer, priceLabel, SKILL_FORMATS, categoryForSkill, readLearnNote } from '../../../util/sweetSkills';
import { variantNameForFormat } from '../../../util/sessionPricing';
import { useListenerReady, ListenerNotReady } from '../../ListenerReadyGate/ListenerReadyGate';
import css from './SkillBookingPanel.module.css';

const QUARTER = 15 * 60 * 1000;
const LISTEN_FORMAT_CARDS = [
  { id: 'chat', label: 'Text', icon: '💬', lines: ['Chat by messaging', 'Great for quick conversations'] },
  { id: 'voice', label: 'Voice', icon: '📞', lines: ['Talk by phone', 'Real-time conversations'] },
  { id: 'video', label: 'Video', icon: '🎥', lines: ['Face-to-face on video', 'More personal conversations'] },
];
const LEAD_MS = 60 * 60 * 1000; // Book a time needs a 1-hour accept lead (server rule).

/**
 * Every open quarter-hour start (at least 15 min free), with how many minutes are free
 * from that start until the open block ends (Sam, Oct 22 2026).
 */
export const openStartTimes = (slots, nowMs = Date.now(), max = 400) => {
  const out = new Map();
  (slots || []).forEach(slot => {
    const a = slot?.attributes || slot || {};
    const s = new Date(a.start).getTime();
    const e = new Date(a.end).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e)) return;
    for (let t = Math.ceil(Math.max(s, nowMs + LEAD_MS) / QUARTER) * QUARTER; t + QUARTER <= e; t += QUARTER) {
      const free = Math.floor((e - t) / 60000);
      out.set(t, Math.max(out.get(t) || 0, free));
    }
  });
  return [...out.entries()].sort((x, y) => x[0] - y[0]).slice(0, max).map(([t, free]) => ({ t, free }));
};

/** Start times (quarter hours) where the whole skill length fits inside an open slot. */
export const skillStartTimes = (slots, minutes, nowMs = Date.now(), max = 24) => {
  const len = minutes * 60000;
  const out = [];
  (slots || []).forEach(slot => {
    const a = slot?.attributes || slot || {};
    const s = new Date(a.start).getTime();
    const e = new Date(a.end).getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e)) return;
    let t = Math.ceil(Math.max(s, nowMs + LEAD_MS) / QUARTER) * QUARTER;
    for (; t + len <= e && out.length < max * 4; t += QUARTER) out.push(t);
  });
  return [...new Set(out)].sort((x, y) => x - y).slice(0, max);
};

/**
 * Sweet Skillz booking: skill -> length -> format -> time. Same checkout as talk sessions;
 * the server prices it from the listing's skillOffers and blocks the full length.
 */
const SkillBookingPanel = ({ listing, online, onSubmit, timeZone, offerId = null, excludeMinutes = [] }) => {
  const dispatch = useDispatch();
  // With offerId (from the "What I offer" menu) the panel books that one offer, Listening included.
  const offers = offerId
    ? offerMenuFromListing(listing)
        .filter(o => o.id === offerId)
        .map(o => {
          const durations = { ...o.durations };
          excludeMinutes.forEach(m => delete durations[String(m)]);
          return { ...o, durations };
        })
        .filter(o => Object.keys(o.durations).length)
    : skillOffersFromListing(listing);
  const fixed = !!offerId;
  const listingId = listing?.id?.uuid;
  const carried = typeof window !== 'undefined' ? readLearnNote(listingId) : null;
  const [freeAsk, setFreeAsk] = useState(null);
  // Sam Oct 27 2026: inline "Use free session" checkbox replaces the Yes/No popup.
  const [useFreeChk, setUseFreeChk] = useState(true);
  const [skillId, setSkillId] = useState(fixed ? offers[0]?.id || null : carried?.skillId && offers.some(o => o.id === carried.skillId) ? carried.skillId : null);
  const [minutes, setMinutes] = useState(null);
  const [format, setFormat] = useState(null);
  // No extra clicks (Sam, Oct 22 2026): preselect the shortest length and first format.
  useEffect(() => {
    const o = offers.find(x => x.id === skillId);
    if (!o || minutes) return;
    const lens = Object.keys(o.durations).map(Number).filter(m => !excludeMinutes.includes(m)).sort((x, y) => x - y);
    if (!lens.length) return;
    setMinutes(15); // Sam Oct 27 2026: 15 minutes only.
    const row = o.durations['15'] || o.durations[String(lens[0])] || {};
    const f = SKILL_FORMATS.find(x => row[x.id] != null);
    if (f && !format) setFormat(f.id);
  }, [skillId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [startMs, setStartMs] = useState(null);
  const [day, setDay] = useState(null);
  const [pickDate, setPickDate] = useState('');
  const [pickTime, setPickTime] = useState('');
  const [checkedMs, setCheckedMs] = useState(null);
  const [note, setNote] = useState(carried?.note || '');
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState(false);
  const listenerReadyFlag = useListenerReady(listing?.id?.uuid);
  const offer = offers.find(o => o.id === skillId) || null;
  // Sam Oct 27 2026: 15 min only. If an offer has no 15-min row, pro-rate its shortest length.
  const row = (() => {
    if (!offer) return null;
    if (offer.durations['15']) return offer.durations['15'];
    const lens = Object.keys(offer.durations).map(Number).sort((x, y) => x - y);
    if (!lens.length) return null;
    const r = offer.durations[String(lens[0])] || {};
    const out = {};
    Object.keys(r).forEach(k => { if (typeof r[k] === 'number') out[k] = Math.round((r[k] * 15) / lens[0]); });
    return out;
  })();
  const price = row && format && row[format] != null ? row[format] : null;
  const hasPrice = price != null;
  const isListen = fixed && isListeningOffer(offer);
  const rate = (isListen && (offer.rate || offer.durations['15'])) || {};
  const fmtLabel = (SKILL_FORMATS.find(f => f.id === format) || {}).label || '';
  // Free first 15 (Talk Now needs only 18+): mark the offer so checkout and the
  // setup gate know this booking expects the free session.
  const currentUser = useSelector(state => state?.user?.currentUser) || null;
  const history = useHistory();
  const isOwnPanel = !!currentUser?.id?.uuid && currentUser.id.uuid === listing?.author?.id?.uuid;
  const freeable = !!(fixed && offer);
  const firstFree = useFreeForListener(freeable ? listing?.author?.id?.uuid || null : null, currentUser);
  const freeOn = !!(freeable && currentUser && (firstFree.free || firstFree.needsVerify));
  const listingUuid = listing?.id?.uuid;
  useEffect(() => {
    if (freeable) rememberFreeOffer(listingUuid, freeOn, firstFree.rewardSubunits || 3000);
  }, [freeable, listingUuid, freeOn, firstFree.rewardSubunits]);
  const freeChosen = freeOn && (minutes !== 15 || useFreeChk);
  // FB bounce fix (Oct 30): logged-out visitors with a free-first-session listener see FREE on
  // the primary button and format cards (they confirm 18+ on /talk-free before starting).
  const guestFree = !!(freeable && !currentUser?.id);
  // Sam Oct 30 1:44 PM: free-eligible viewers on Talk never see $ amounts; FREE only until first free is used.
  const hidePrices = !!(isListen && (freeOn || guestFree));
  const showFree = hidePrices || (minutes === 15 && (freeChosen || (guestFree && useFreeChk)));
  const bookLabel =
    showFree
      ? `Book ${fmtLabel} Session — FREE`
      : freeChosen
      ? `Book ${fmtLabel} Session — ${priceLabel(price)} (first 15 min free)`
      : `Book ${fmtLabel} Session — ${priceLabel(price)}`;

  useEffect(() => {
    if (!listingId || !minutes) return;
    const start = new Date();
    const end = new Date(Date.now() + 14 * 24 * 3600 * 1000);
    dispatch((d, getState, sdk) =>
      sdk.timeslots
        .query({ listingId: listing.id, start, end, perPage: 500 })
        .then(res => setSlots(res?.data?.data || []))
        .catch(() => setSlots([]))
    );
  }, [listingId, minutes, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const opens = useMemo(() => (slots ? openStartTimes(slots) : []), [slots]);
  const times = opens.map(o => o.t);
  // Lengths this offer has for the picked format (or any format), shortest first.
  const offeredLens = offer
    ? Object.keys(offer.durations).map(Number).filter(m => !format || offer.durations[String(m)][format] != null).sort((x, y) => x - y)
    : [];
  const freeAt = t => (opens.find(o => o.t === t) || {}).free || 0;
  const longestFit = t => offeredLens.filter(m => m <= freeAt(t)).pop() || null;
  const fitLabel = t => {
    const fit = longestFit(t);
    if (!fit) return 'too short';
    return fit === offeredLens[offeredLens.length - 1] ? `up to ${fit} min` : fit === offeredLens[0] ? `${fit} min only` : `up to ${fit} min`;
  };
  const startFits = startMs && minutes ? freeAt(startMs) >= minutes : false;
  if (!offers.length) return null;

  const fmtTime = ms =>
    new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });

  // Sam 8:03 PM: eligible talkers choose first: free 15 min (18+ only) or paid (ID + card).
  const submit = (now, useFree) => {
    void 0;
    if (!offer || !minutes || !format || typeof onSubmit !== 'function') return;
    // Sam 9:29 PM Oct 23: a guest on a listening Talk Now signs up first, then comes back
    // here (auto-continues) so the Yes/No free prompt is asked with their account.
    if (!currentUser?.id && freeable && now && history) {
      const m = window.location.pathname.match(/^(\/l\/[^/]+\/[0-9a-f-]{36})/);
      if (m) {
        const back = `${m[1]}/book?mode=now&resume=1`;
        saveBookingReturnPath(back);
        history.push('/talk-free', { from: back });
        return;
      }
    }
    if (freeOn && minutes === 15 && useFree === undefined) {
      useFree = !!useFreeChk;
    }
    const takeFree = freeOn && minutes === 15 && useFree !== false;
    // Never book a length that doesn't fit the open block.
    if (!now && startMs && freeAt(startMs) < minutes) return;
    const base = {
      sessionFormat: format,
      priceVariantName: variantNameForFormat(format),
      skillId: offer.id,
      skillLabel: offer.label,
      skillMinutes: minutes,
      ...(note.trim() ? { learnNote: note.trim().slice(0, 300) } : {}),
    };
    setBusy(true);
    try {
      // The setup gate runs before checkout stores its data: leave a one-tab hint
      // that this is a free Talk Now start (server still re-checks everything).
      if (now && takeFree) {
        window.sessionStorage.setItem('sl_free_now_intent', String(listingUuid));
        window.localStorage.setItem('sl_free_now_intent', String(Date.now()));
      } else {
        window.sessionStorage.removeItem('sl_free_now_intent');
        window.localStorage.removeItem('sl_free_now_intent');
      }
      if (useFree === false) rememberFreeOffer(listingUuid, false, 0);
    } catch (e) {
      // storage blocked: gate falls back to the setup step
    }
    try {
      // Sam 8:23 PM Oct 23: remember this exact checkout so setup (email/ID/18+) always returns here.
      const m = window.location.pathname.match(/^(\/l\/[^/]+\/[0-9a-f-]{36})/);
      if (m) window.localStorage.setItem('sl_resume_checkout', JSON.stringify({ path: `${m[1]}/checkout`, at: Date.now() }));
    } catch (e) {}
    if (now) {
      const s = Math.ceil((Date.now() + 20000) / QUARTER) * QUARTER;
      onSubmit({ ...base, ...(useFree === false ? { declineFree: true } : {}), startNow: true, bookingStartTime: String(s), bookingEndTime: String(s + minutes * 60000) });
    } else if (startMs) {
      onSubmit({ ...base, ...(useFree === false ? { declineFree: true } : {}), bookingStartTime: String(startMs), bookingEndTime: String(startMs + minutes * 60000) });
    }
    window.setTimeout(() => setBusy(false), 1600);
  };

  // Sam 9:29 PM Oct 23: back from signup (?resume=1) → continue the Talk Now tap once.
  const [resumed, setResumed] = useState(false);
  const [waitDone, setWaitDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setWaitDone(true), 3000);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (resumed || !currentUser?.id || typeof window === 'undefined') return;
    if (!/[?&]resume=1\b/.test(window.location.search || '')) return;
    if (!offer || !minutes || !format) return;
    if (freeable && !(firstFree.free || firstFree.needsVerify) && !waitDone) return;
    setResumed(true);
    submit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id?.uuid, offer?.id, minutes, format, firstFree.free, firstFree.needsVerify, waitDone, resumed]);

  // Sam Oct 25: same server onboarding guard as checkout. Listeners who can't take bookings yet
  // (payouts / ID / email / guidelines unfinished) get no times or Book button here.
  // Sam Oct 25: you can't book yourself.
  if (isOwnPanel) {
    return (
      <section className={css.root} data-skill-booking="true" data-own-listing="true">
        <p>This is your own profile, so booking is turned off here.</p>
      </section>
    );
  }
  if (!isOwnPanel && listenerReadyFlag === false) {
    return (
      <section className={css.root} data-skill-booking="true" data-listener-ready="false">
        <ListenerNotReady />
      </section>
    );
  }

  return (
    <section className={css.root} data-skill-booking="true" data-shared-booking-card="true" data-offer-panel={offer?.id || ''}>
      {/* Sam Oct 27 2026 9:38 PM: one shared booking card for talk + learn; show what they're booking. */}
      <h3 className={css.title}>Book a 15-min session</h3>
      {offer ? <p className={css.bookingWhat} data-booking-what="true">You’re booking: <b>{isListeningOffer(offer) ? 'Talk it out' : offer.label}</b></p> : null}
      {freeAsk ? (
        <div className={css.freeAskBackdrop} role="dialog" aria-modal="true" aria-labelledby="sl-free-ask-title" data-free-ask="true">
          <div className={css.freeAskBox}>
            <h2 id="sl-free-ask-title" className={css.freeAskTitle}>Would you like to use your free 15-minute session?</h2>
            <p className={css.freeAskText}>{freeAsk.now ? 'Yes needs only a quick 18+ confirm.' : 'Yes needs an 18+ confirm and Apple Pay, Google Pay or a card, used only for no-shows or late cancels.'} No books a paid session. You pay with Apple Pay, Google Pay or a card at checkout.</p>
            <button type="button" className={css.freeAskYes} data-free-ask-yes="true" onClick={() => { const n = freeAsk.now; setFreeAsk(null); submit(n, true); }}>Yes, use my free session</button>
            <button type="button" className={css.freeAskNo} data-free-ask-no="true" onClick={() => { const n = freeAsk.now; setFreeAsk(null); submit(n, false); }}>No, book a paid session</button>
          </div>
        </div>
      ) : null}
      {fixed ? null : <p className={css.step}>1. Pick a skill</p>}
      {fixed ? null : <div className={css.chips}>
        {offers.map(o => {
          const cat = categoryForSkill(o.id);
          return (
            <button type="button" key={o.id} className={o.id === skillId ? css.on : css.chip} onClick={() => { setSkillId(o.id); setMinutes(null); setFormat(null); setStartMs(null); }}>
              {cat ? cat.emoji : '✨'} {o.label}
            </button>
          );
        })}
      </div>}
      {/* Sam Oct 27 2026 9:39 PM: talker types what they want to talk about / learn. */}
      <label className={css.topicField} data-booking-topic="true">
        <span>What do you want to talk about or learn?</span>
        <input type="text" maxLength={120} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. a rough week at work, guitar basics, a job interview" />
      </label>
      {isListen ? (
        <div className={css.connect} data-listen-formats="true">
          <h4 className={css.connectTitle}>Choose how you’d like to connect</h4>
          <div className={css.fmtCards}>
            {LISTEN_FORMAT_CARDS.filter(f => rate[f.id] != null).map(f => (
              <button type="button" key={f.id} className={[css.fmtCard, css[`fmt_${f.id}`], format === f.id ? css.fmtOn : ''].join(' ')} onClick={() => setFormat(f.id)} aria-pressed={format === f.id} data-listen-format={f.id}>
                <span className={css.fmtRadio} aria-hidden="true" />
                <span className={css.fmtIcon} aria-hidden="true">{f.icon}</span>
                <b className={css.fmtName}>{f.label}</b>
                <span className={css.fmtPrice} data-fmt-free={showFree ? 'true' : undefined}>{showFree ? 'FREE' : `$${rate[f.id]}`}</span>
                <span className={css.fmtPer}>{showFree ? 'first 15 min' : 'per 15 min'}</span>
                <ul className={css.fmtList}>{f.lines.map(l => <li key={l}>{l}</li>)}</ul>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {row && !isListen ? (
        <div className={css.connect} data-learn-formats="true">
          <h4 className={css.connectTitle}>Choose how you’d like to connect</h4>
          <div className={css.fmtCards}>
            {LISTEN_FORMAT_CARDS.filter(f => row[f.id] != null).map(f => (
              <button type="button" key={f.id} className={[css.fmtCard, css[`fmt_${f.id}`], format === f.id ? css.fmtOn : ''].join(' ')} onClick={() => setFormat(f.id)} aria-pressed={format === f.id} data-learn-format={f.id}>
                <span className={css.fmtRadio} aria-hidden="true" />
                <span className={css.fmtIcon} aria-hidden="true">{f.icon}</span>
                <b className={css.fmtName}>{f.label}</b>
                <span className={css.fmtPrice}>{priceLabel(row[f.id])}</span>
                <span className={css.fmtPer}>per 15 min</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {hasPrice ? (
        <>
          
          {(freeOn || guestFree) && minutes === 15 && !hidePrices ? (
            <label className={css.freeChk} data-free-check="true">
              <input type="checkbox" checked={useFreeChk} onChange={e => setUseFreeChk(e.target.checked)} />
              <span>Use my free session <small>(first 15 min, $0)</small></span>
            </label>
          ) : null}
          {/* Sam Oct 27 2026: Start now always shown; live only when green Available. */}
          <button type="button" className={[css.primary, online ? '' : css.primaryOff].join(' ')} disabled={busy || !online} onClick={() => submit(true)} data-skill-now={online ? 'true' : 'off'} title={online ? undefined : 'Start now opens when they’re green Available'}>
            {online ? (showFree ? `Start now · FREE ${fmtLabel} session (15 min)` : `Start now · ${bookLabel}`) : (showFree ? 'Start now · FREE' : 'Start now')}
          </button>
          {online ? null : <p className={css.muted} data-now-off="true">Not available right now — book for later below.</p>}
          <p className={css.step} data-later-head="true">Or book for later</p>
          <div className={css.when} data-when-picker="true">
            {/* Pick your own time (Sam, Oct 22 2026): date + 15-min time, then Check availability. */}
            {slots == null ? <p className={css.muted}>Finding open times…</p> : (() => {
              const pad = n => String(n).padStart(2, '0');
              const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
              const today = new Date();
              const lastDay = new Date(Date.now() + 13 * 864e5);
              const quarters = [];
              for (let m = 0; m < 24 * 60; m += 15) quarters.push(`${pad(Math.floor(m / 60))}:${pad(m % 60)}`);
              const timeLabel = hm => new Date(`2000-01-01T${hm}:00`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              const fmtShort = t => new Date(t).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
              const check = () => {
                if (!pickDate || !pickTime) return;
                const t = new Date(`${pickDate}T${pickTime}:00`).getTime();
                setCheckedMs(t);
                setStartMs(minutes && freeAt(t) >= minutes ? t : freeAt(t) >= 15 ? t : null);
              };
              const free = checkedMs ? freeAt(checkedMs) : 0;
              const fits = checkedMs && minutes && free >= minutes;
              const shorter = checkedMs && !fits ? longestFit(checkedMs) : null;
              const nearest = checkedMs && !fits && minutes
                ? opens.filter(o => o.free >= minutes).sort((a, b) => Math.abs(a.t - checkedMs) - Math.abs(b.t - checkedMs)).slice(0, 3).sort((a, b) => a.t - b.t)
                : [];
              return (
                <div className={css.pickBox}>
                  <div className={css.pickRow}>
                    <label className={css.whenField}>
                      <span>Date</span>
                      <input type="date" min={ymd(today)} max={ymd(lastDay)} value={pickDate} onChange={e => { setPickDate(e.target.value); setCheckedMs(null); setStartMs(null); }} data-when-date="true" />
                    </label>
                    <label className={css.whenField}>
                      <span>Time</span>
                      <select value={pickTime} onChange={e => { setPickTime(e.target.value); setCheckedMs(null); setStartMs(null); }} data-when-time="true">
                        <option value="">Pick a time</option>
                        {quarters.map(hm => <option key={hm} value={hm}>{timeLabel(hm)}</option>)}
                      </select>
                    </label>
                  </div>
                  <button type="button" className={css.checkBtn} disabled={!pickDate || !pickTime} onClick={check} data-when-check="true">
                    Check availability
                  </button>
                  {checkedMs && fits ? <p className={css.openYes} data-when-open="true">Open! {fmtShort(checkedMs)} · {minutes} min</p> : null}
                  {checkedMs && !fits ? (
                    <div className={css.fitNote} role="status" data-fit-note="true">
                      <p>{shorter ? `Only ${shorter} minutes ${shorter === 15 ? 'is' : 'are'} open at this time.` : 'That time isn’t open.'}</p>
                      {shorter ? (
                        <button type="button" className={css.fitSwitch} onClick={() => { setStartMs(checkedMs); }} data-fit-switch={shorter}>
                          Switch to {shorter} min
                        </button>
                      ) : null}
                      {nearest.length ? (
                        <div className={css.nearest} data-when-nearest="true">
                          <span>Nearest open times for {minutes} min:</span>
                          {nearest.map(o => (
                            <button type="button" key={o.t} className={css.nearBtn} onClick={() => { setCheckedMs(o.t); setStartMs(o.t); const d = new Date(o.t); setPickDate(ymd(d)); setPickTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`); }}>
                              {fmtShort(o.t)}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })()}
          </div>
          {startMs && startFits && checkedMs ? (
            <button type="button" className={css.primary} disabled={busy} onClick={() => submit(false)} data-skill-book="true">
              {`${bookLabel} · ${fmtTime(startMs)}`}
            </button>
          ) : null}
          {freeable ? <p className={css.muted} data-charge-note="true">You won’t be charged until your session is confirmed.</p> : null}
          {freeChosen && price > 0 && minutes > 15 ? <p className={css.free}>Your first 15 minutes are free. On longer sessions, the first 15 minutes come off the price.</p> : null}
        </>
      ) : null}
    </section>
  );
};

export default SkillBookingPanel;
